import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { saveOnboardingResumePath } from "./onboardingSession";
import { loadItem, storageKeys } from "../../shared/storage/index";
import { ONBOARDING_SHEET_CLOSE_MS } from "./onboardingSheetConstants";
import "./OnboardingSheet.css";

const SHEET_CLOSE_MS = ONBOARDING_SHEET_CLOSE_MS;

const OnboardingSheetContext = createContext(null);

export function useOnboardingSheet() {
  const context = useContext(OnboardingSheetContext);
  if (!context) {
    throw new Error("useOnboardingSheet must be used within OnboardingSheet");
  }
  return context;
}

function restorePageScroll() {
  document.documentElement.classList.remove("onboarding-sheet-open");
  document.body.style.overflow = "";
}

export default function OnboardingSheet({
  children,
  onRequestHold,
  onReleaseHold,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isClosingRef = useRef(false);
  const panelRef = useRef(null);
  const requestCloseRef = useRef(() => {});

  const finishClose = useCallback((to) => {
    onRequestHold?.();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        navigate(to, {
          replace: true,
          state: to.startsWith("/profile-details")
            ? { instantTab: true, freshProfile: true }
            : { instantTab: true },
        });

        requestAnimationFrame(() => {
          onReleaseHold?.();
          window.setTimeout(restorePageScroll, 32);
        });
      });
    });
  }, [navigate, onRequestHold, onReleaseHold]);

  const requestCloseTo = useCallback((to = "/") => {
    if (isClosingRef.current) return;

    isClosingRef.current = true;
    const panel = panelRef.current;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      panel?.removeEventListener("transitionend", onTransitionEnd);
      window.clearTimeout(fallbackTimer);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          finishClose(to);
        });
      });
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== "transform") return;
      finish();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel?.addEventListener("transitionend", onTransitionEnd);
        setIsClosing(true);
      });
    });

    const fallbackTimer = window.setTimeout(finish, SHEET_CLOSE_MS + 80);
  }, [finishClose]);

  const requestClose = useCallback(() => {
    if (isClosingRef.current) return;

    if (location.state?.mode === "edit" || loadItem(storageKeys.onboardingComplete)) {
      requestCloseTo("/profile-details");
      return;
    }
    if (!loadItem(storageKeys.onboardingComplete)) {
      saveOnboardingResumePath(location.pathname);
    }
    requestCloseTo("/");
  }, [location.pathname, location.state, requestCloseTo]);

  requestCloseRef.current = requestClose;

  useEffect(() => {
    document.documentElement.classList.add("onboarding-sheet-open");
    document.body.style.overflow = "hidden";

    const enterTimer = window.setTimeout(() => {
      setIsAnimatedIn(true);
    }, 20);

    const onKey = (event) => {
      if (event.key === "Escape" && !isClosingRef.current) {
        requestCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.clearTimeout(enterTimer);
      window.removeEventListener("keydown", onKey);
      if (!isClosingRef.current) {
        restorePageScroll();
      }
    };
  }, []);

  useEffect(() => {
    if (isAnimatedIn) return undefined;

    const recoverTimer = window.setTimeout(() => {
      setIsAnimatedIn(true);
    }, 48);

    return () => window.clearTimeout(recoverTimer);
  }, [isAnimatedIn]);

  const value = useMemo(
    () => ({
      requestClose,
      requestCloseTo,
    }),
    [requestClose, requestCloseTo],
  );

  const rootClass = [
    "onboarding-sheet-root",
    isAnimatedIn || isClosing ? "onboarding-sheet-root--open" : "",
    isClosing ? "onboarding-sheet-root--closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const panelClass = [
    "onboarding-sheet-panel",
    !isAnimatedIn && !isClosing ? "onboarding-sheet-panel--enter-pending" : "",
    isClosing ? "onboarding-sheet-panel--closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <OnboardingSheetContext.Provider value={value}>
      <div className={rootClass} role="presentation">
        <button
          type="button"
          className="onboarding-sheet-backdrop"
          aria-label="Close registration"
          onClick={requestClose}
          disabled={isClosing}
        />
        <div ref={panelRef} className={panelClass} role="dialog" aria-modal="true">
          {children}
        </div>
      </div>
    </OnboardingSheetContext.Provider>,
    document.body,
  );
}
